import { Grid, Stack, Typography, MenuItem, ListItemText, Box } from '@mui/material';
import { DeleteForever as RemoveIcon } from '@mui/icons-material';

import {
  TextField,
  IconButton,
  Select,
  ColorPickerWithLabel,
  InfoOutlinedIcon,
} from '@occc-app/design-system';
import { OfferFilter, Random_Award_Type } from '@occc/graphql';
import { Tooltip } from '@occc/ui-design-system';

import { segmentIconSelectItems, segmentTypeSelectItems } from '../constants';
import { OfferSelect } from '../../../CampaignComponents/components/OfferSelect';
import { parseNumber } from '../../../../lib/finalFormUtils';

const SegmentColorConfiguration = (props: { index: number }) => {
  const { index } = props;
  return <ColorPickerWithLabel name={`segments[${index}].color`} label='Segment Color' />;
};

const SegmentTypeConfiguration = (props: { index: number; onChanged(): void }) => {
  const { index, onChanged } = props;
  return (
    <Select
      name={`segments[${index}].type`}
      label='Member Wins'
      data={segmentTypeSelectItems}
      fieldProps={{}}
      onChange={onChanged}
    />
  );
};

const SegmentIconConfiguration = (props: { index: number }) => {
  const { index } = props;
  return (
    <Select
      name={`segments[${index}].icon`}
      label='Segment Icon'
      formControlProps={{ sx: { width: 200 } }}
    >
      {segmentIconSelectItems.map((item) => (
        <MenuItem key={item.value} value={item.value}>
          <Stack direction='row' alignItems='center' spacing={1}>
            {item.icon}
            <ListItemText>{item.label}</ListItemText>
          </Stack>
        </MenuItem>
      ))}
    </Select>
  );
};

const SegmentValueConfiguration = (props: {
  segmentType: Random_Award_Type;
  index: number;
  makeOffersFilter: () => OfferFilter;
}) => {
  const { index, makeOffersFilter, segmentType } = props;
  if (segmentType === Random_Award_Type.Points) {
    return (
      <TextField
        type='number'
        name={`segments[${index}].awardPoints`}
        label='Amount'
        fieldProps={{ parse: parseNumber }}
      />
    );
  }

  if (segmentType === Random_Award_Type.Vouchers) {
    return (
      <OfferSelect
        makeOffersFilter={makeOffersFilter}
        name={`segments[${index}].awardOffer`}
        label='Award'
      />
    );
  }
  return null;
};

const SegmentLimitConfiguration = (props: { index: number }) => {
  const { index } = props;
  return (
    <TextField
      type='number'
      name={`segments[${index}].limit`}
      label='Award Limits'
      fieldProps={{ parse: parseNumber }}
    />
  );
};

const SegmentExpirationDateConfiguration = (props: { index: number }) => {
  const { index } = props;
  return (
    <TextField
      type='number'
      name={`segments[${index}].awardOfferExpirationDays`}
      label='Award Expiration Days'
      fieldProps={{ parse: parseNumber }}
    />
  );
};

const SegmentVoucherCountConfiguration = (props: { index: number }) => {
  const { index } = props;
  return (
    <TextField
      type='number'
      name={`segments[${index}].awardOfferVoucherCount`}
      label='Awards Per Segment'
      fieldProps={{ parse: parseNumber }}
    />
  );
};

type SegmentConfigurationProps = {
  index: number;
  onRemove(): void;
  onTypeChanged(): void;
  makeOffersFilter: () => OfferFilter;
  segmentType: Random_Award_Type;
  isDeleteButtonDisabled?: boolean;
};
export const SegmentConfiguration = (props: SegmentConfigurationProps) => {
  const { index, isDeleteButtonDisabled, onRemove, onTypeChanged, makeOffersFilter, segmentType } =
    props;

  return (
    <Stack spacing={2}>
      <Stack direction='row' spacing={1} alignItems='center' pl={1}>
        <Typography fontWeight='bold'>Segment {index + 1}</Typography>
        {segmentType === Random_Award_Type.Vouchers && (
          <Tooltip title='Award must be published to be selected'>
            <span tabIndex={0}>
              <InfoOutlinedIcon fontSize='small' sx={{ color: 'silver' }} />
            </span>
          </Tooltip>
        )}
        <IconButton disabled={isDeleteButtonDisabled} onClick={onRemove} size='small'>
          <RemoveIcon color={isDeleteButtonDisabled ? 'disabled' : 'error'} />
        </IconButton>
      </Stack>
      <Box>
        <Grid container columnSpacing={1} alignItems='flex-start'>
          <Grid item xs={2}>
            <SegmentTypeConfiguration index={index} onChanged={onTypeChanged} />
          </Grid>
          <Grid item xs={3}>
            {[Random_Award_Type.Vouchers, Random_Award_Type.Points].includes(segmentType) && (
              <SegmentValueConfiguration
                index={index}
                makeOffersFilter={makeOffersFilter}
                segmentType={segmentType}
              />
            )}
          </Grid>
          <Grid item xs={2}>
            <SegmentLimitConfiguration index={index} />
          </Grid>
          <Grid item xs={2}>
            {[Random_Award_Type.Vouchers].includes(segmentType) && (
              <SegmentVoucherCountConfiguration index={index} />
            )}
          </Grid>
          <Grid item xs={3}>
            {[Random_Award_Type.Vouchers].includes(segmentType) && (
              <SegmentExpirationDateConfiguration index={index} />
            )}
          </Grid>
        </Grid>
      </Box>
      <Stack direction='row' spacing={2}>
        {[Random_Award_Type.Vouchers].includes(segmentType) && (
          <SegmentIconConfiguration index={index} />
        )}
        <SegmentColorConfiguration index={index} />
      </Stack>
    </Stack>
  );
};
